import React, { useCallback } from 'react';
import { AuthLayout } from 'app/components/auth/AuthLayout';
import { Button, Select, styled, Text } from '@goodrx/matisse';
import { PageHeading } from 'app/components/PageHeading';
import { Divider } from 'app/components/core';
import { useSynapse } from 'app/lib/synaptik';
import { states as stateOptions } from 'app/data';
import { ServiceData } from '@goodrx/heydoctor-sdk/es/models/service';
import { DateOfBirth, Gender, OnboardingField, OnboardingFieldProps } from '../Onboarding';
import { useOnboardingContext, useOnboardingValidation } from '../Onboarding/hooks';
import { CtaContainer } from '../SignUp/CtaContainer';
import { SignInCta } from '../SignUp/SignInCta';
import { trackEvent } from 'app/lib/analytics';

const StyledPageHeading = styled(PageHeading)`
  p {
    font-weight: 500;
  }
`;

const StyledDivider = styled(Divider)<{ marginBottom: number }>`
  margin-top: 24px;
  margin-bottom: ${(p) => (Number.isInteger(p.marginBottom) ? `${p.marginBottom}px` : '24px')};
`;

const CurrentState: React.FunctionComponent<OnboardingFieldProps> = ({ onChange, values, error }) => {
  const [validateServiceStateAvailability, isValidatingServiceStateAvailability] = useSynapse(({ services }) => [
    services.validateServiceAvailability,
    services.state.validatingServiceAvailability,
  ]);
  const handleChange = useCallback(
    (_: string, state: string) => {
      onChange({
        values: { currentState: state },
        validate: async () => {
          const isValid = await validateServiceStateAvailability({ state });
          if (!isValid) {
            return { valid: false, errorMessage: 'This service is currently unavailable in your state' };
          }
          return { valid: true };
        },
      });
    },
    [onChange, validateServiceStateAvailability]
  );

  return (
    <>
      <Text as="h3" fontSize={18} mt={3} mb={2}>
        Current location
      </Text>
      <Select
        onChange={handleChange}
        value={values.currentState}
        name="state"
        label="State"
        floating
        options={stateOptions}
        placeholder="State"
        disabled={isValidatingServiceStateAvailability}
      />
      {error && (
        <Text color="red" fontWeight={500} marginTop="8" fontSize="14px">
          {error}
        </Text>
      )}
    </>
  );
};

interface EcommTestEligibilityProps {
  onContinue: () => void;
  service: ServiceData;
}

export const EcommTestEligibility: React.FunctionComponent<EcommTestEligibilityProps> = ({ onContinue, service }) => {
  const [isValidatingServiceAvailability] = useSynapse(({ services }) => [
    services.state.validatingServiceAvailability,
  ]);

  const { isValidDateOfBirth, isValidGender } = useOnboardingValidation();

  const { profile, errors } = useOnboardingContext();

  const isValidStateForService = profile.currentState && !errors.currentState;

  const handleContinue = useCallback(() => {
    trackEvent('Registration CTA Clicked', { buttonName: 'Continue', page: 'Eligibility' });
    onContinue();
  }, [onContinue]);

  return (
    <>
      <CtaContainer>
        <SignInCta small />
      </CtaContainer>
      <AuthLayout>
        <StyledPageHeading
          title="Eligibility Verification"
          subtitle="Let’s confirm that you’re eligible for this prescription."
          color="greyDarkest"
          mb={0}
        />
        <StyledDivider />

        <Gender service={service} hideDescription />
        <OnboardingField errorFieldName="currentState" fields={['currentState']}>
          {(props) => <CurrentState {...props} />}
        </OnboardingField>

        <DateOfBirth service={service} />
        <StyledDivider marginBottom={0} />
        <Button
          variant="primary"
          size="lg"
          block
          my={4}
          onClick={handleContinue}
          loading={isValidatingServiceAvailability}
          disabled={!isValidDateOfBirth || !isValidGender || !isValidStateForService}>
          Continue
        </Button>
      </AuthLayout>
    </>
  );
};
